import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket, useSocketEvent } from '@/lib/socket';
import type {
    TaskCreatedEvent,
    TaskUpdatedEvent,
    TaskDeletedEvent,
    TaskMovedEvent,
    TaskAssignedEvent,
    TaskUnassignedEvent,
    ProjectDataImportedEvent,
    CommentAddedEvent,
    CommentUpdatedEvent,
    CommentDeletedEvent,
    AttachmentAddedEvent,
    AttachmentDeletedEvent,
    ProjectDeletedEvent,
} from '@/lib/socket';
import { taskKeys } from './use-tasks';
import { commentKeys } from './use-comments';
import { attachmentKeys } from './use-attachments';
import { viewKeys } from './use-views';
import { customFieldKeys } from './use-custom-fields';
import { projectKeys } from './use-projects';
import { useAuthStore } from '@/stores/auth-store';

// ============================================
// REAL-TIME TASK UPDATES HOOK
// ============================================

export function useRealtimeTasks(projectId: string | undefined) {
    const queryClient = useQueryClient();
    const { joinProject, leaveProject } = useSocket();

    // Join/leave project room
    useEffect(() => {
        if (projectId) {
            joinProject(projectId);
            return () => leaveProject(projectId);
        }
    }, [projectId, joinProject, leaveProject]);

    // Handle task created event
    const handleTaskCreated = useCallback(
        (data: TaskCreatedEvent) => {
            if (data.task.projectId === projectId) {
                // Invalidate ALL task list queries for this project (regardless of filters)
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
                // Also invalidate myTasks since the task might be assigned
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'my'],
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle task updated event
    const handleTaskUpdated = useCallback(
        (data: TaskUpdatedEvent) => {
            if (data.task.projectId === projectId) {
                // Invalidate ALL task list queries for this project (regardless of filters)
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
                queryClient.invalidateQueries({
                    queryKey: taskKeys.detail(data.task.id),
                });
                // Also invalidate myTasks since status/assignment might have changed
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'my'],
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle task deleted event
    const handleTaskDeleted = useCallback(
        (data: TaskDeletedEvent) => {
            // Invalidate ALL task list queries for this project (regardless of filters)
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
            }
            // Also invalidate myTasks
            queryClient.invalidateQueries({
                queryKey: ['tasks', 'my'],
            });
            // Remove from cache
            queryClient.removeQueries({
                queryKey: taskKeys.detail(data.taskId),
            });
        },
        [queryClient, projectId]
    );

    // Handle task moved event
    const handleTaskMoved = useCallback(
        (_data: TaskMovedEvent) => {
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle task status event (t2): the backend emits a dedicated
    // 'task:status' broadcast when a task transitions between statuses
    // (TODO / IN_PROGRESS / DONE / CANCELLED). Gantt/Kanban/Tables all
    // group by or render based on status, so any cached task list needs
    // to drop. Task detail cache also needs to refresh so the side panel
    // reflects the new status immediately.
    const handleTaskStatus = useCallback(
        (data: { taskId: string; projectId: string; status: string; previousStatus?: string }) => {
            if (projectId && data.projectId === projectId) {
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
                queryClient.invalidateQueries({
                    queryKey: taskKeys.detail(data.taskId),
                });
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'my'],
                });
            }
        },
        [queryClient, projectId]
    );

    // t14: dependency:created / dependency:deleted. The Gantt SVG
    // overlay + task-detail "Depends on" section both read from the
    // same query key, so a single invalidation covers all open
    // views. Scope check matches the broadcast — only act on events
    // for the active project.
    //
    // R7 / t33: added a "sticky" cache check for dependency:deleted —
    // if the deleted id is already gone from the local cache, skip
    // the invalidation. The originating user's optimistic remove
    // applies before the WS event loops back, so the cache already
    // reflects the deletion.
    //
    // R8 / t34: full self-echo filter via `createdBy` / `deletedBy`.
    // The backend now emits the originating user's id on every
    // dependency:created / dependency:deleted broadcast (see
    // backend/src/routes/task-dependencies.ts). When the WS event
    // matches the current user we treat the local optimistic update
    // as the source of truth and skip ALL work — the optimistic
    // `onMutate` already applied the change, and `onSettled` will
    // refetch once the HTTP round-trip completes. This eliminates
    // the residual "dep disappears → reappears → disappears"
    // flicker where the WS invalidate raced the optimistic onSettled
    // refetch. Other users still see normal propagation because
    // their actor id differs.
    const handleDependencyChanged = useCallback(
        (data: {
            id?: string;
            projectId?: string;
            createdBy?: string;
            deletedBy?: string;
        }) => {
            if (!projectId) return;
            if (data.projectId && data.projectId !== projectId) return;
            // R8: actor-id-based self-echo filter. If the WS event
            // originated from the current user, the local optimistic
            // patch is already correct — skip the invalidate entirely.
            const currentUserId = useAuthStore.getState().user?.id;
            const actorId = data.createdBy ?? data.deletedBy;
            if (
                currentUserId &&
                actorId &&
                actorId === currentUserId
            ) {
                return;
            }
            // R10 / t38: defensive sticky check on OTHER users'
            // DELETEs. If the cache already reflects the deletion
            // (e.g. the dep was removed earlier and the WS event
            // we're handling now is a re-broadcast) we don't need
            // to invalidate. Only applies to deletes — for creates
            // we must invalidate so the new dep shows up even if
            // it wasn't in the local cache yet.
            if (data.deletedBy && data.id) {
                const cache = queryClient.getQueryData<{
                    dependencies: { id: string }[];
                    total: number;
                }>(['taskDependencies', 'list', projectId]);
                const stillThere = !!cache?.dependencies?.some((d) => d.id === data.id);
                if (!stillThere) return;
            }
            queryClient.invalidateQueries({
                queryKey: ['taskDependencies', 'list', projectId],
            });
        },
        [queryClient, projectId]
    );

    // Handle task assigned event
    const handleTaskAssigned = useCallback(
        (data: TaskAssignedEvent) => {
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
                queryClient.invalidateQueries({
                    queryKey: taskKeys.detail(data.taskId),
                });
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'my'],
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle task unassigned event
    const handleTaskUnassigned = useCallback(
        (data: TaskUnassignedEvent) => {
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
                queryClient.invalidateQueries({
                    queryKey: taskKeys.detail(data.taskId),
                });
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'my'],
                });
            }
        },
        [queryClient, projectId]
    );

    const handleProjectDataImported = useCallback(
        (data: ProjectDataImportedEvent) => {
            if (data.projectId !== projectId) return;
            queryClient.invalidateQueries({ queryKey: ['tasks', 'list', projectId] });
            queryClient.invalidateQueries({ queryKey: ['tasks', 'my'] });
            queryClient.invalidateQueries({ queryKey: customFieldKeys.list(projectId) });
        },
        [queryClient, projectId]
    );

    // Handle comment added event - update task list to reflect new comment count
    const handleCommentAddedForList = useCallback(
        (data: CommentAddedEvent) => {
            if (projectId) {
                // Invalidate task list to update comment counts
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle comment deleted event - update task list to reflect new comment count
    const handleCommentDeletedForList = useCallback(
        (data: CommentDeletedEvent) => {
            if (projectId) {
                // Invalidate task list to update comment counts
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle attachment added event - update task list to reflect new attachment count
    const handleAttachmentAddedForList = useCallback(
        (data: AttachmentAddedEvent) => {
            if (projectId) {
                // Invalidate task list to update attachment counts
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle attachment deleted event - update task list to reflect new attachment count
    const handleAttachmentDeletedForList = useCallback(
        (data: AttachmentDeletedEvent) => {
            if (projectId) {
                // Invalidate task list to update attachment counts
                queryClient.invalidateQueries({
                    queryKey: ['tasks', 'list', projectId],
                });
            }
        },
        [queryClient, projectId]
    );

    // Subscribe to events
    useSocketEvent<TaskCreatedEvent>('task:created', handleTaskCreated, [projectId]);
    useSocketEvent<TaskUpdatedEvent>('task:updated', handleTaskUpdated, [projectId]);
    useSocketEvent<TaskDeletedEvent>('task:deleted', handleTaskDeleted, [projectId]);
    useSocketEvent<TaskMovedEvent>('task:moved', handleTaskMoved, [projectId]);
    useSocketEvent<{ taskId: string; projectId: string; status: string; previousStatus?: string }>('task:status', handleTaskStatus, [projectId]);
    // t14: dependency broadcast events. Same handler is fine for both
    // because the only thing we do is invalidate the list query.
    // R8 / t34: the payloads now carry `createdBy` / `deletedBy`
    // (the originating user's id) so the realtime handler can
    // detect self-echo and skip the invalidate.
    useSocketEvent<{
        id: string;
        projectId: string;
        type?: string;
        predecessorId?: string;
        successorId?: string;
        createdBy?: string;
    }>('dependency:created', handleDependencyChanged, [projectId]);
    useSocketEvent<{
        id: string;
        projectId: string;
        deletedBy?: string;
    }>('dependency:deleted', handleDependencyChanged, [projectId]);
    useSocketEvent<TaskAssignedEvent>('task:assigned', handleTaskAssigned, [projectId]);
    useSocketEvent<TaskUnassignedEvent>('task:unassigned', handleTaskUnassigned, [projectId]);
    useSocketEvent<ProjectDataImportedEvent>('project:dataImported', handleProjectDataImported, [projectId]);
    useSocketEvent<CommentAddedEvent>('comment:added', handleCommentAddedForList, [projectId]);
    useSocketEvent<CommentDeletedEvent>('comment:deleted', handleCommentDeletedForList, [projectId]);
    useSocketEvent<AttachmentAddedEvent>('attachment:added', handleAttachmentAddedForList, [projectId]);
    useSocketEvent<AttachmentDeletedEvent>('attachment:deleted', handleAttachmentDeletedForList, [projectId]);

    // Handle view created event. R1 fix: invalidate every type-filtered
    // variant for the project (KANBAN / CALENDAR / GANTT / TABLE / ALL)
    // — the previous viewKeys.list(projectId) call only matched the
    // ['views','list', projectId, 'ALL'] 4-tuple, so a KanbanView or
    // GanttView with the matching sidebar mounted would NOT refresh
    // until the next manual reload. viewKeys.listAll(projectId) is the
    // ['views','list', projectId] 3-tuple prefix that matches all
    // type variants via React Query's default prefix semantics.
    const handleViewCreated = useCallback(
        () => {
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: viewKeys.listAll(projectId),
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle view updated event
    const handleViewUpdated = useCallback(
        () => {
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: viewKeys.listAll(projectId),
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle view deleted event
    const handleViewDeleted = useCallback(
        (data: { viewId: string }) => {
            if (projectId) {
                queryClient.invalidateQueries({ queryKey: viewKeys.listAll(projectId) });
                queryClient.removeQueries({ queryKey: viewKeys.detail(data.viewId) });
            }
        },
        [queryClient, projectId]
    );

    // Handle view duplicated event
    const handleViewDuplicated = useCallback(
        () => {
            if (projectId) {
                queryClient.invalidateQueries({ queryKey: viewKeys.listAll(projectId) });
            }
        },
        [queryClient, projectId]
    );

    // Generic column-event handler: every column mutation broadcasts to
    // project:<id>. We invalidate every list variant for the project so
    // any mounted KanbanView (sidebar, board, modal) refreshes itself
    // without a manual reload. viewIdChanged is forwarded so consumers can
    // re-key their local activeView reference (F2 fix from t1 review).
    // R1 fix: use viewKeys.listAll(projectId) so type-filtered variants
    // (KANBAN / CALENDAR / GANTT / TABLE) also get invalidated — the
    // previous viewKeys.list(projectId) only matched the unfiltered
    // ['views','list', projectId, 'ALL'] key.
    //
    // R3 fix: the originating user receives their own broadcast back, so
    // reordering a column on the board triggered a round-trip
    // invalidate → refetch → "the whole screen reloads" flicker. We
    // compare the event's `<verb>By` field against the current user's
    // id and skip the LIST-cache invalidation when it matches; other
    // users still sync normally because their ids differ.
    //
    // R4 fix: dropped the detail-cache invalidation path entirely. The
    // detail cache (`viewKeys.detail`) is read by no component (every
    // consumer reads from the LIST cache via `useSavedViews`), so the
    // invalidation was a no-op for the kanban board — but on rare
    // timing edges it triggered a refetch that pulled data the
    // originating user's mutation had just written, racing the
    // optimistic onMutate patch and rolling the local cache back to the
    // pre-reorder order. Now the only cache-side effect on self-echo
    // is "do nothing" — the optimistic onMutate + withCachePatch path
    // in useReorderColumns owns the local cache update. The fork path
    // (viewIdChanged) still removes the stale detail row for
    // previousViewId when an auto-clone forks off a new viewId; that's a
    // removeQueries (targeted drop, no refetch) and never affects the
    // LIST cache the board reads from.
    const handleColumnMutated = useCallback(
        (data: {
            viewIdChanged?: string;
            previousViewId?: string;
            viewId?: string;
            createdBy?: string;
            updatedBy?: string;
            deletedBy?: string;
            reorderedBy?: string;
        }) => {
            const currentUserId = useAuthStore.getState().user?.id;
            const actorId =
                data.createdBy ?? data.updatedBy ?? data.deletedBy ?? data.reorderedBy;
            const isSelfEcho = !!currentUserId && !!actorId && actorId === currentUserId;
            if (data.viewIdChanged && data.previousViewId && data.previousViewId !== data.viewIdChanged) {
                // Targeted drop of the now-stale detail row after a
                // non-owner auto-clone forks off a new viewId. No
                // refetch — just remove the cache entry so any
                // future `useViewDetail`-style consumer doesn't read
                // the ghost row.
                queryClient.removeQueries({ queryKey: viewKeys.detail(data.previousViewId) });
            }
            // LIST refresh is the heavy one (refetches all
            // ['views','list', projectId, *] variants). Skip it on
            // self-echo so the user's own action doesn't refetch the
            // entire view-list cache behind their drag — the optimistic
            // onMutate + withCachePatch already updated the local cache
            // when they dropped the column.
            if (!isSelfEcho && projectId) {
                queryClient.invalidateQueries({ queryKey: viewKeys.listAll(projectId) });
            }
        },
        [queryClient, projectId]
    );

    // Subscribe to view events
    useSocketEvent('view:created', handleViewCreated, [projectId]);
    useSocketEvent('view:updated', handleViewUpdated, [projectId]);
    useSocketEvent('view:deleted', handleViewDeleted, [projectId]);
    useSocketEvent('view:duplicated', handleViewDuplicated, [projectId]);
    // Column CRUD broadcasts — every one fires the same invalidation
    // pattern so a sibling Kanban board refreshes itself in real time.
    useSocketEvent('column:created', handleColumnMutated, [projectId]);
    useSocketEvent('column:updated', handleColumnMutated, [projectId]);
    useSocketEvent('column:deleted', handleColumnMutated, [projectId]);
    useSocketEvent('column:reordered', handleColumnMutated, [projectId]);
}

// ============================================
// REAL-TIME TASK DETAIL UPDATES HOOK
// ============================================

export function useRealtimeTaskDetail(taskId: string | undefined) {
    const queryClient = useQueryClient();
    const { joinTask, leaveTask } = useSocket();

    // Join/leave task room for detailed updates
    useEffect(() => {
        if (taskId) {
            joinTask(taskId);
            return () => leaveTask(taskId);
        }
    }, [taskId, joinTask, leaveTask]);

    // Handle comment added event
    const handleCommentAdded = useCallback(
        (data: CommentAddedEvent) => {
            if (data.taskId === taskId) {
                queryClient.invalidateQueries({
                    queryKey: commentKeys.list(taskId),
                });
                // Also update task detail for comment count
                queryClient.invalidateQueries({
                    queryKey: taskKeys.detail(taskId),
                });
            }
        },
        [queryClient, taskId]
    );

    // Handle comment updated event
    const handleCommentUpdated = useCallback(
        (data: CommentUpdatedEvent) => {
            if (data.taskId === taskId) {
                queryClient.invalidateQueries({
                    queryKey: commentKeys.list(taskId),
                });
            }
        },
        [queryClient, taskId]
    );

    // Handle comment deleted event
    const handleCommentDeleted = useCallback(
        (data: CommentDeletedEvent) => {
            if (data.taskId === taskId) {
                queryClient.invalidateQueries({
                    queryKey: commentKeys.list(taskId),
                });
                queryClient.invalidateQueries({
                    queryKey: taskKeys.detail(taskId),
                });
            }
        },
        [queryClient, taskId]
    );

    // Handle attachment added event
    const handleAttachmentAdded = useCallback(
        (data: AttachmentAddedEvent) => {
            if (data.taskId === taskId) {
                queryClient.invalidateQueries({
                    queryKey: attachmentKeys.list(taskId),
                });
                queryClient.invalidateQueries({
                    queryKey: taskKeys.detail(taskId),
                });
            }
        },
        [queryClient, taskId]
    );

    // Handle attachment deleted event
    const handleAttachmentDeleted = useCallback(
        (data: AttachmentDeletedEvent) => {
            if (data.taskId === taskId) {
                queryClient.invalidateQueries({
                    queryKey: attachmentKeys.list(taskId),
                });
                queryClient.invalidateQueries({
                    queryKey: taskKeys.detail(taskId),
                });
            }
        },
        [queryClient, taskId]
    );

    // Subscribe to events
    useSocketEvent<CommentAddedEvent>('comment:added', handleCommentAdded, [taskId]);
    useSocketEvent<CommentUpdatedEvent>('comment:updated', handleCommentUpdated, [taskId]);
    useSocketEvent<CommentDeletedEvent>('comment:deleted', handleCommentDeleted, [taskId]);
    useSocketEvent<AttachmentAddedEvent>('attachment:added', handleAttachmentAdded, [taskId]);
    useSocketEvent<AttachmentDeletedEvent>('attachment:deleted', handleAttachmentDeleted, [taskId]);
}

// ============================================
// TYPING INDICATOR HOOK
// ============================================

import { useState } from 'react';
import type { TypingEvent } from '@/lib/socket';

interface TypingUser {
    userId: string;
    userName: string;
}

export function useTypingIndicator(taskId: string | undefined) {
    const [typingUsers, setTypingUsers] = useState<Map<string, TypingUser>>(new Map());
    const { startTyping, stopTyping } = useSocket();

    // Handle typing start event
    const handleTypingStart = useCallback(
        (data: TypingEvent) => {
            if (data.taskId === taskId && data.userName) {
                setTypingUsers((prev) => {
                    const next = new Map(prev);
                    next.set(data.userId, { userId: data.userId, userName: data.userName! });
                    return next;
                });

                // Auto-remove after 3 seconds if no stop event
                setTimeout(() => {
                    setTypingUsers((prev) => {
                        const next = new Map(prev);
                        next.delete(data.userId);
                        return next;
                    });
                }, 3000);
            }
        },
        [taskId]
    );

    // Handle typing stop event
    const handleTypingStop = useCallback(
        (data: TypingEvent) => {
            if (data.taskId === taskId) {
                setTypingUsers((prev) => {
                    const next = new Map(prev);
                    next.delete(data.userId);
                    return next;
                });
            }
        },
        [taskId]
    );

    // Subscribe to events
    useSocketEvent<TypingEvent>('typing:start', handleTypingStart, [taskId]);
    useSocketEvent<TypingEvent>('typing:stop', handleTypingStop, [taskId]);

    // Helper to emit typing events
    const emitTypingStart = useCallback(() => {
        if (taskId) {
            startTyping(taskId);
        }
    }, [taskId, startTyping]);

    const emitTypingStop = useCallback(() => {
        if (taskId) {
            stopTyping(taskId);
        }
    }, [taskId, stopTyping]);

    return {
        typingUsers: Array.from(typingUsers.values()),
        isTyping: typingUsers.size > 0,
        emitTypingStart,
        emitTypingStop,
    };
}

// ============================================
// REAL-TIME CUSTOM FIELD UPDATES HOOK
// ============================================

export function useRealtimeCustomFields(projectId: string | undefined) {
    const queryClient = useQueryClient();

    // Handle custom field created event
    const handleCustomFieldCreated = useCallback(
        () => {
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: customFieldKeys.list(projectId),
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle custom field updated event
    const handleCustomFieldUpdated = useCallback(
        () => {
            if (projectId) {
                queryClient.invalidateQueries({
                    queryKey: customFieldKeys.list(projectId),
                });
            }
        },
        [queryClient, projectId]
    );

    // Handle custom field deleted event
    const handleCustomFieldDeleted = useCallback(
        () => {
            if (projectId) {
                void queryClient.invalidateQueries({ queryKey: ["tasks"] });
                queryClient.invalidateQueries({
                    queryKey: customFieldKeys.list(projectId),
                });
            }
        },
        [queryClient, projectId]
    );

    // Subscribe to events
    useSocketEvent('customField:created', handleCustomFieldCreated, [projectId]);
    useSocketEvent('customField:updated', handleCustomFieldUpdated, [projectId]);
    useSocketEvent('customField:deleted', handleCustomFieldDeleted, [projectId]);
}

// ============================================
// REAL-TIME PROJECT EVENTS (GLOBAL)
// ============================================

export function useRealtimeProjects() {
    const queryClient = useQueryClient();

    // Handle project deleted event - invalidate projects lists and favorites
    const handleProjectDeleted = useCallback(
        (data: ProjectDeletedEvent) => {
            // Invalidate all project lists
            queryClient.invalidateQueries({
                queryKey: projectKeys.lists(),
            });
            // Invalidate favorites (important for navbar)
            queryClient.invalidateQueries({
                queryKey: ['projects', 'favorites'],
            });
            // Remove specific project from cache
            queryClient.removeQueries({
                queryKey: projectKeys.detail(data.projectId),
            });

            // If user is viewing this project, redirect to projects page
            const currentPath = window.location.pathname;
            if (currentPath.includes(`/projects/${data.projectId}`)) {
                window.location.href = '/projects';
            }
        },
        [queryClient]
    );

    // Subscribe to project deleted event - pass queryClient in deps so callback is recreated when it changes
    useSocketEvent<ProjectDeletedEvent>('project:deleted', handleProjectDeleted, [queryClient]);
}
